import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  type ClipboardScorePenaltyMultipliers,
  type ClipboardScorePolicy,
  type EmergencyCreditStats,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { snakeCase } from "lodash";
import { useState } from "react";

import { areMultipliersActive } from "../../utils/utils";
import { ActiveMultiplierInfo } from "../ActiveMultiplierInfo";
import { CancellationCreditStatusCard } from "./CancellationCreditStatusCard";
import { LatenessCreditStatusCard } from "./LatenessCreditStatusCard";
import { AdjustmentsSection, type AdjustmentsSectionProps } from "./Section";
import { ShortLeadTimeShiftInfo } from "./ShortLeadTimeShiftInfo";
import {
  calculateCreditInfo,
  getCancellationItems,
  getNoShowItems,
  getTimelinessItems,
  getWorkingItems,
} from "./utils";

interface ClipboardScoreAdjustmentsContentProps {
  policy: ClipboardScorePolicy;
  multipliers?: ClipboardScorePenaltyMultipliers;
  useExpectedArrivalTime: boolean;
  emergencyCreditStats?: EmergencyCreditStats;
  isEmergencyCreditsEnabled?: boolean;
}

interface GetSectionsParams {
  policy: ClipboardScorePolicy;
  multipliers?: ClipboardScorePenaltyMultipliers;
  useExpectedArrivalTime?: boolean;
  emergencyCreditStats?: EmergencyCreditStats;
  isEmergencyCreditsEnabled?: boolean;
}

function getSections(params: GetSectionsParams) {
  const {
    policy,
    multipliers,
    useExpectedArrivalTime = false,
    emergencyCreditStats,
    isEmergencyCreditsEnabled = false,
  } = params;

  const workingItems = getWorkingItems(policy);
  const noShowItems = getNoShowItems();

  // Lateness credit calculations
  const latenessCredit = calculateCreditInfo(
    emergencyCreditStats?.lateness,
    isEmergencyCreditsEnabled
  );

  const timelinessItems = getTimelinessItems({
    policy,
    multiplier: multipliers?.lateness.next,
    showCreditInfo: latenessCredit.shouldShowCreditInfo,
    hasCredit: latenessCredit.hasCredit,
  });

  // Cancellation credit calculations
  const cancellationCredit = calculateCreditInfo(
    emergencyCreditStats?.cancellation,
    isEmergencyCreditsEnabled
  );

  const cancellationItems = getCancellationItems({
    policy,
    multiplier: multipliers?.cancellation.next,
    showCreditInfo: cancellationCredit.shouldShowCreditInfo,
    hasCredit: cancellationCredit.hasCredit,
  });

  const sections: Array<
    Pick<
      AdjustmentsSectionProps,
      "title" | "description" | "items" | "callout" | "hasContentContainer" | "variant"
    >
  > = [];

  if (workingItems.length > 0) {
    sections.push({
      title: "Work the full shift",
      items: workingItems,
    });
  }

  if (timelinessItems.length > 0) {
    const getTimelinessCallout = (): React.ReactNode => {
      if (latenessCredit.shouldShowCreditInfo) {
        return (
          <LatenessCreditStatusCard
            hasCredit={latenessCredit.hasCredit}
            shiftsUntilNextCredit={latenessCredit.shiftsUntilNextCredit}
          />
        );
      }

      if (useExpectedArrivalTime) {
        return <ShortLeadTimeShiftInfo />;
      }

      return undefined;
    };

    sections.push({
      title: "Show up on time",
      items: timelinessItems,
      callout: getTimelinessCallout(),
      hasContentContainer: latenessCredit.shouldShowCreditInfo,
    });
  }

  if (noShowItems.length > 0) {
    sections.push({
      title: "Never no show",
      items: noShowItems,
    });
  }

  if (cancellationItems.length > 0) {
    const getCancellationSectionTitle = (): string => {
      if (!cancellationCredit.shouldShowCreditInfo) {
        return "Cancel early";
      }

      return cancellationCredit.hasCredit ? "Cancel at least a day in advance" : "Cancel Early";
    };

    sections.push({
      title: getCancellationSectionTitle(),
      description: cancellationCredit.shouldShowCreditInfo
        ? undefined
        : "The earlier you cancel, the fewer points you lose",
      items: cancellationItems,
      callout: cancellationCredit.shouldShowCreditInfo ? (
        <CancellationCreditStatusCard
          hasCredit={cancellationCredit.hasCredit}
          shiftsUntilNextCredit={cancellationCredit.shiftsUntilNextCredit}
        />
      ) : undefined,
      hasContentContainer: cancellationCredit.shouldShowCreditInfo,
      variant: "text" as const,
    });
  }

  return sections;
}

export function ClipboardScoreAdjustmentsContent({
  policy,
  multipliers,
  useExpectedArrivalTime,
  emergencyCreditStats,
  isEmergencyCreditsEnabled,
}: ClipboardScoreAdjustmentsContentProps) {
  const sections = getSections({
    policy,
    multipliers,
    useExpectedArrivalTime,
    emergencyCreditStats,
    isEmergencyCreditsEnabled,
  });

  const [expanded, setExpanded] = useState<string | boolean>(false);

  return (
    <Stack direction="column" spacing={6} paddingTop={5} padding={3}>
      {isDefined(multipliers) && areMultipliersActive(multipliers) && (
        <Stack spacing={7} padding={7} justifyContent="center" alignItems="center">
          <ActiveMultiplierInfo multipliers={multipliers} />
        </Stack>
      )}
      {sections.map((section) => {
        const { title } = section;
        const sectionId = snakeCase(title);

        return (
          <AdjustmentsSection
            key={`adjustment-section-${sectionId}`}
            expanded={expanded === sectionId}
            onClick={() => {
              setExpanded(expanded === sectionId ? false : sectionId);
            }}
            {...section}
          />
        );
      })}
    </Stack>
  );
}
